import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import type { PaperTag } from "@/types/paper";

export function useTags(conceptId: string) {
  return useQuery({
    queryKey: ["tags", conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/tags`)
        .json<PaperTag[]>(),
    enabled: !!conceptId,
  });
}

export function useTagActions(conceptId: string) {
  const queryClient = useQueryClient();

  const createTag = useMutation({
    mutationFn: (data: { name: string; color: string }) =>
      apiClient
        .post(`api/concepts/${conceptId}/tags`, { json: data })
        .json<PaperTag>(),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: ["tags", conceptId] });
      const previous = queryClient.getQueryData<PaperTag[]>(["tags", conceptId]);
      // Optimistic tag with temporary ID
      const optimistic: PaperTag = {
        id: `temp-${Date.now()}`,
        name: data.name,
        color: data.color,
        paper_count: 0,
      };
      queryClient.setQueryData<PaperTag[]>(
        ["tags", conceptId],
        (old) => [...(old ?? []), optimistic].sort((a, b) => a.name.localeCompare(b.name)),
      );
      return { previous, optimisticId: optimistic.id };
    },
    onSuccess: (newTag, _vars, context) => {
      // Replace temp tag with real one
      queryClient.setQueryData<PaperTag[]>(
        ["tags", conceptId],
        (old) =>
          (old ?? []).map((t) => (t.id === context?.optimisticId ? newTag : t)),
      );
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["tags", conceptId], context.previous);
      }
    },
  });

  const updateTag = useMutation({
    mutationFn: ({
      tagId,
      ...data
    }: {
      tagId: string;
      name?: string;
      color?: string;
    }) =>
      apiClient
        .patch(`api/concepts/${conceptId}/tags/${tagId}`, { json: data })
        .json<PaperTag>(),
    onMutate: async ({ tagId, ...data }) => {
      await queryClient.cancelQueries({ queryKey: ["tags", conceptId] });
      await queryClient.cancelQueries({ queryKey: ["papers", conceptId] });
      const previous = queryClient.getQueryData<PaperTag[]>(["tags", conceptId]);
      queryClient.setQueryData<PaperTag[]>(
        ["tags", conceptId],
        (old) =>
          (old ?? []).map((t) =>
            t.id === tagId ? { ...t, ...data } : t,
          ).sort((a, b) => a.name.localeCompare(b.name)),
      );
      // Update tag data embedded in papers so table dots reflect the change
      queryClient.setQueriesData<{ papers: Array<{ tags?: PaperTag[] }> }>(
        { queryKey: ["papers", conceptId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            papers: old.papers.map((p) => ({
              ...p,
              tags: p.tags?.map((t) =>
                t.id === tagId ? { ...t, ...data } : t,
              ),
            })),
          };
        },
      );
      return { previous };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["tags", conceptId], context.previous);
      }
    },
  });

  const deleteTag = useMutation({
    mutationFn: (tagId: string) =>
      apiClient.delete(`api/concepts/${conceptId}/tags/${tagId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tags", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
    },
  });

  return { createTag, updateTag, deleteTag };
}

export function useUpdatePaperTags(conceptId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { paperId: string; tagIds: string[] }) =>
      apiClient
        .put(`api/concepts/${conceptId}/tags/papers/${data.paperId}`, {
          json: { tag_ids: data.tagIds },
        })
        .json<PaperTag[]>(),
    onMutate: async ({ paperId, tagIds }) => {
      await queryClient.cancelQueries({ queryKey: ["papers", conceptId] });
      const allTags = queryClient.getQueryData<PaperTag[]>(["tags", conceptId]) ?? [];
      const tagSet = new Set(tagIds);
      const resolvedTags = allTags.filter((t) => tagSet.has(t.id));
      // Optimistically update papers cache so table dots refresh immediately
      queryClient.setQueriesData<{ papers: Array<{ id: string; tags?: PaperTag[] }> }>(
        { queryKey: ["papers", conceptId] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            papers: old.papers.map((p) =>
              p.id === paperId ? { ...p, tags: resolvedTags } : p,
            ),
          };
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      queryClient.invalidateQueries({ queryKey: ["tags", conceptId] });
    },
  });
}
